import React, { useEffect, useRef, useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  TradeFilterSelect,
  TradeFilterSelectFullWidth,
  TradeFilterSearchableSelectFullWidth,
  TradeMultiFilterSelect,
} from "../../../components/trade-filter-selects";
import { EditIcon } from "../../../components/ui/edit";
import { GripVerticalIcon } from "../../../components/ui/grip-vertical";
import { TrashIcon } from "../../../components/ui/trash";
import type { TradePolicyAction, TradePolicyCatalogField, TradePolicyGroupNode } from "../../../types/tradePolicy";
import { SimulatorInlineEditActions } from "./simulatorInlineEdit";
import { SimulatorPolicyAddButton } from "./SimulatorPolicyAddButton";
import {
  catalogEnumValuesForPicker,
  EditorGroupNode,
  EditorNode,
  EditorRuleNode,
  GROUP_POLICY_ACTION_OPTIONS,
  isPolicyCombineShellGroup,
} from "./policyTreeUtils";
import { formatPolicyActionDisplayLabel, PolicyActionField } from "./PolicyActionField";
import {
  PolicyRuleFieldExplanationBlock,
  type PolicyRuleFieldExplanation,
} from "./policyFieldExplanation";
import { PolicyRuleNumericValueInputs } from "./policyRuleNumericValue";
import {
  POLICY_TREE_FIELD_LABEL_CLASS,
  POLICY_TREE_GROUP_TITLE_CLASS,
  POLICY_TREE_HELPER_TEXT_CLASS,
  POLICY_TREE_INPUT_CLASS,
  POLICY_TREE_RULE_TITLE_CLASS,
  POLICY_TREE_SUBTLE_TEXT_CLASS,
} from "./policyTreeTheme";
import { policyCatalogFieldDisplayLabel } from "../../../utils/policyCatalogFieldLabels";
import {
  applyTradeDirectionSelect,
  catalogFieldPickerOptions,
  enumInSelectedTokens,
  formatOperatorLabel,
  policyRuleValueHelperText,
  tradeDirectionSelectValue,
  tradeTypeAxis,
} from "./policyTreeRuleHelpers";

const TRADE_LOG_LOSS_RED = "#E00101";

const POLICY_ACCORDION_TRIGGER_CLASS =
  "w-full items-center py-3 font-medium hover:no-underline [&_[data-slot=accordion-trigger-icon]]:shrink-0";
const POLICY_NESTED_ACCORDION_CLASS = "w-full border-none";

function syncSiblingAccordionOpen(
  siblings: EditorNode[],
  openIds: string[],
  setCollapsedNodeIds: React.Dispatch<React.SetStateAction<Set<string>>>
): void {
  const openSet = new Set(openIds);
  setCollapsedNodeIds((prev) => {
    const next = new Set(prev);
    for (const node of siblings) {
      if (openSet.has(node.id)) next.delete(node.id);
      else next.add(node.id);
    }
    return next;
  });
}

export type PolicyTreeEditorProps = {
  nodes: EditorNode[];
  catalogFields: TradePolicyCatalogField[];
  catalogFieldsForPicker: TradePolicyCatalogField[];
  fieldsByPath: Map<string, TradePolicyCatalogField>;
  ruleLeaves: number;
  collapsedNodeIds: Set<string>;
  setCollapsedNodeIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  canRemoveGroup: boolean;
  readOnly?: boolean;
  /** Hide veto/keep group action controls (Exit Placement SL/TP policies). */
  hideGroupAction?: boolean;
  /** Fallback group title in read-only views (e.g. policy version label). */
  defaultGroupLabel?: string;
  nested?: boolean;
  onUpdateRule: (id: string, patch: Partial<EditorRuleNode>) => void;
  onRulePathChange: (id: string, path: string) => void;
  onRuleOpChange: (id: string, op: string) => void;
  onGroupOpChange: (id: string, op: TradePolicyGroupNode["op"]) => void;
  onGroupActionChange: (id: string, action: TradePolicyAction) => void;
  defaultGroupAction: TradePolicyAction;
  onGroupLabelChange: (id: string, label: string) => void;
  onGoToSourcePick: (pickId: string) => void;
  onRemove: (id: string) => void;
  onAddRule: (groupId: string) => void;
  draggedRuleId: string | null;
  onRuleDragStart: (ruleId: string, e: React.DragEvent<HTMLElement>) => void;
  onRuleDragEnd: () => void;
  onGroupDragOver: (groupId: string, e: React.DragEvent<HTMLDivElement>) => void;
  onGroupDropRule: (groupId: string, e: React.DragEvent<HTMLDivElement>) => void;
  numericHintForPath: (path: string) => string | null;
  fieldExplanationFor: (field: TradePolicyCatalogField) => PolicyRuleFieldExplanation;
};

function PolicyRuleEditorBody({
  node,
  props,
}: {
  node: EditorRuleNode;
  props: PolicyTreeEditorProps;
}): React.ReactElement {
  const {
    catalogFieldsForPicker,
    fieldsByPath,
    onUpdateRule,
    onRulePathChange,
    onRuleOpChange,
    numericHintForPath,
    fieldExplanationFor,
    readOnly = false,
  } = props;
  const field = fieldsByPath.get(node.path);
  const isEnum = field?.value_kind === "enum";
  const isInOp = node.op === "in";
  const typeAxis = field ? tradeTypeAxis(field) : null;
  const isTradeTypeDirection = Boolean(typeAxis);
  const enumPickerValues = catalogEnumValuesForPicker(field?.enum_values, [node.valueEnum, ...node.valueEnumSet]);
  const ruleValueHelper = isTradeTypeDirection
    ? null
    : policyRuleValueHelperText(node, field, Boolean(isEnum), isInOp, enumPickerValues, numericHintForPath);
  const fieldExplanation = field && node.path.trim() ? fieldExplanationFor(field) : null;

  return (
    <>
      <div className="w-full min-w-0">
        <div className="grid w-full min-w-0 grid-cols-1 gap-3 md:grid-cols-3">
          <Field className="min-w-0 w-full gap-1.5">
            <FieldLabel className={POLICY_TREE_FIELD_LABEL_CLASS}>Field</FieldLabel>
            <TradeFilterSearchableSelectFullWidth
              value={node.path}
              onValueChange={(value) => onRulePathChange(node.id, value)}
              options={catalogFieldPickerOptions(catalogFieldsForPicker, node.path, fieldsByPath)}
              placeholder="Search fields…"
              aria-label="Field"
              disabled={readOnly}
            />
          </Field>

          {isTradeTypeDirection && typeAxis ? (
            <Field className="min-w-0 w-full gap-1.5 md:col-span-2">
              <FieldLabel className={POLICY_TREE_FIELD_LABEL_CLASS}>Direction</FieldLabel>
              <TradeFilterSelectFullWidth
                value={tradeDirectionSelectValue(node, typeAxis)}
                onValueChange={(value) => applyTradeDirectionSelect(node.id, value, typeAxis, onUpdateRule)}
                options={[
                  { value: "", label: "Select" },
                  { value: "long", label: "Long" },
                  { value: "short", label: "Short" },
                  { value: "both", label: "Both", disabled: !field?.allowed_ops.includes("in") },
                ]}
                placeholder="Select"
                disabled={readOnly}
              />
            </Field>
          ) : (
            <>
              <Field className="min-w-0 w-full gap-1.5">
                <FieldLabel className={POLICY_TREE_FIELD_LABEL_CLASS}>Operator</FieldLabel>
                {(() => {
                  const ops = [...(field?.allowed_ops ?? [])];
                  if (field?.value_kind === "number") {
                    const canRange = ops.includes("gte") || ops.includes("lte");
                    if (canRange) ops.unshift("range");
                  }
                  const seen = new Set<string>();
                  const uniqueOps = ops.filter((x) => (seen.has(x) ? false : (seen.add(x), true)));
                  return (
                    <TradeFilterSelectFullWidth
                      value={node.op}
                      onValueChange={(value) => onRuleOpChange(node.id, value)}
                      options={uniqueOps.map((op) => ({ value: op, label: formatOperatorLabel(op) }))}
                      placeholder="Select operator"
                      disabled={readOnly}
                    />
                  );
                })()}
              </Field>

              <Field className="min-w-0 w-full gap-1.5">
                <FieldLabel className={POLICY_TREE_FIELD_LABEL_CLASS}>Value</FieldLabel>
                {isEnum && isInOp && enumPickerValues.length > 0 ? (
                  <div
                    data-trade-filter-bar=""
                    data-trade-filter-bar-layout="full"
                    className={cn("w-full min-w-0", readOnly && "pointer-events-none")}
                  >
                    <TradeMultiFilterSelect
                      layout="full"
                      options={enumPickerValues}
                      selected={enumInSelectedTokens(node)}
                      emptyLabel="Select..."
                      onToggle={(token) => {
                        const current = enumInSelectedTokens(node);
                        const next = current.includes(token)
                          ? current.filter((t) => t !== token)
                          : [...current, token];
                        onUpdateRule(node.id, { valueEnum: "", valueEnumSet: next });
                      }}
                    />
                  </div>
                ) : isEnum && enumPickerValues.length > 0 ? (
                  <TradeFilterSelectFullWidth
                    value={node.valueEnum}
                    onValueChange={(value) => onUpdateRule(node.id, { valueEnum: value, valueEnumSet: [] })}
                    options={enumPickerValues.map((token) => ({ value: token, label: token }))}
                    placeholder="Select..."
                    disabled={readOnly}
                  />
                ) : isEnum && isInOp ? (
                  <Input
                    value={node.valueEnum !== "" ? node.valueEnum : node.valueEnumSet.join(", ")}
                    onChange={(e) => onUpdateRule(node.id, { valueEnum: e.target.value, valueEnumSet: [] })}
                    placeholder="Comma-separated, e.g. trend, down"
                    disabled={readOnly}
                    className={POLICY_TREE_INPUT_CLASS}
                  />
                ) : isEnum ? (
                  <Input
                    value={node.valueEnum}
                    onChange={(e) => onUpdateRule(node.id, { valueEnum: e.target.value })}
                    placeholder={
                      enumPickerValues.length
                        ? `e.g. ${enumPickerValues[0]}`
                        : "Catalog enum token (lowercase)"
                    }
                    disabled={readOnly}
                    className={POLICY_TREE_INPUT_CLASS}
                  />
                ) : field?.value_kind === "number" && field ? (
                  <PolicyRuleNumericValueInputs
                    field={field}
                    op={node.op}
                    value={node.valueNumber}
                    valueMin={(node as EditorRuleNode & { valueNumberMin?: string }).valueNumberMin ?? ""}
                    valueMax={(node as EditorRuleNode & { valueNumberMax?: string }).valueNumberMax ?? ""}
                    onValueChange={(value) => onUpdateRule(node.id, { valueNumber: value })}
                    onValueMinChange={(value) => onUpdateRule(node.id, { valueNumberMin: value } as Partial<EditorRuleNode>)}
                    onValueMaxChange={(value) => onUpdateRule(node.id, { valueNumberMax: value } as Partial<EditorRuleNode>)}
                    readOnly={readOnly}
                  />
                ) : (
                  <Input
                    value={node.valueEnum}
                    onChange={(e) => onUpdateRule(node.id, { valueEnum: e.target.value })}
                    placeholder="Value"
                    disabled={readOnly}
                    className={POLICY_TREE_INPUT_CLASS}
                  />
                )}
              </Field>
            </>
          )}
        </div>
        {fieldExplanation ? <PolicyRuleFieldExplanationBlock explanation={fieldExplanation} /> : null}
        {ruleValueHelper ? <p className={POLICY_TREE_HELPER_TEXT_CLASS}>{ruleValueHelper}</p> : null}
      </div>
    </>
  );
}

function PolicyRuleAccordionItem({
  node,
  props,
}: {
  node: EditorRuleNode;
  props: PolicyTreeEditorProps;
}): React.ReactElement {
  const {
    fieldsByPath,
    ruleLeaves,
    draggedRuleId,
    onRemove,
    onRuleDragStart,
    onRuleDragEnd,
    readOnly = false,
  } = props;
  const dragPreviewElRef = useRef<HTMLElement | null>(null);
  const field = fieldsByPath.get(node.path);
  const fieldLabel = field
    ? policyCatalogFieldDisplayLabel(node.path, field.label)
    : node.path.trim()
      ? policyCatalogFieldDisplayLabel(node.path)
      : "Select field";
  const isDraggingThisRule = draggedRuleId === node.id;

  return (
    <AccordionItem value={node.id} data-policy-tree-node="rule" className="border-0 bg-transparent shadow-none">
      <AccordionTrigger className={POLICY_ACCORDION_TRIGGER_CLASS}>
        <div
          data-policy-tree-trigger-main=""
          className="flex min-w-0 flex-1 items-center justify-between gap-2"
          style={{ opacity: isDraggingThisRule ? 0 : 1 }}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {!readOnly ? (
              <span
                className={cn("inline-flex items-center justify-center text-gray-500 dark:text-white", "cursor-grab")}
                draggable
                aria-label="Drag rule"
                title="Drag rule"
                onDragStart={(e) => {
                  onRuleDragStart(node.id, e);
                  e.stopPropagation();
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", node.id);
                  const sourceEl = e.currentTarget.closest("[data-slot=accordion-item]") as HTMLElement | null;
                  if (!sourceEl) return;
                  try {
                    const rect = sourceEl.getBoundingClientRect();
                    const clone = sourceEl.cloneNode(true) as HTMLElement;
                    clone.style.position = "fixed";
                    clone.style.top = "-9999px";
                    clone.style.left = "-9999px";
                    clone.style.width = `${rect.width}px`;
                    clone.style.maxWidth = `${rect.width}px`;
                    clone.style.margin = "0";
                    clone.style.pointerEvents = "none";
                    clone.style.opacity = "1";
                    clone.style.transform = "none";
                    clone.style.zIndex = "2147483647";
                    document.body.appendChild(clone);
                    dragPreviewElRef.current = clone;
                    e.dataTransfer.setDragImage(clone, Math.min(24, rect.width / 2), Math.min(24, rect.height / 2));
                  } catch {
                    // Fallback to browser default drag image.
                  }
                }}
                onDragEnd={() => {
                  if (dragPreviewElRef.current) {
                    dragPreviewElRef.current.remove();
                    dragPreviewElRef.current = null;
                  }
                  onRuleDragEnd();
                }}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <GripVerticalIcon size={14} aria-hidden />
              </span>
            ) : null}
            <span className={POLICY_TREE_RULE_TITLE_CLASS}>{`Rule | ${fieldLabel}`}</span>
          </div>
          {!readOnly ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 shrink-0 text-[#E00101] hover:text-[#E00101]"
              aria-label="Remove rule"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(node.id);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={ruleLeaves <= 1}
            >
              <TrashIcon size={18} aria-hidden />
            </Button>
          ) : null}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pt-0 pb-0 text-foreground">
        <PolicyRuleEditorBody node={node} props={props} />
      </AccordionContent>
    </AccordionItem>
  );
}

function PolicyGroupAccordionItem({
  node,
  props,
}: {
  node: EditorGroupNode;
  props: PolicyTreeEditorProps;
}): React.ReactElement {
  const {
    canRemoveGroup,
    onGroupOpChange,
    onGroupActionChange,
    defaultGroupAction,
    onGroupLabelChange,
    onGoToSourcePick,
    onRemove,
    onAddRule,
    draggedRuleId,
    onGroupDragOver,
    onGroupDropRule,
    readOnly = false,
    hideGroupAction = false,
  } = props;
  const groupLabelEditRef = useRef<HTMLDivElement | null>(null);
  const [isEditingGroupLabel, setIsEditingGroupLabel] = useState(false);
  const [groupLabelDraft, setGroupLabelDraft] = useState("");

  useEffect(() => {
    if (!isEditingGroupLabel) setGroupLabelDraft(node.label ?? "");
  }, [node.label, isEditingGroupLabel]);

  const groupTitle = (() => {
    const label = (node.label ?? "").trim();
    if (label && label !== "Group") return label;
    if (!props.nested && isPolicyCombineShellGroup(node)) return "Combined policies";
    if (!props.nested) return props.defaultGroupLabel?.trim() || "Group";
    return "Group";
  })();
  const hideActionForNode = hideGroupAction || isPolicyCombineShellGroup(node);
  const canJumpToSourcePick = Boolean(node.autoLabelFromPick && node.sourcePickId && groupTitle !== "Group");

  const endGroupLabelEdit = () => {
    setGroupLabelDraft(node.label ?? "");
    setIsEditingGroupLabel(false);
  };

  const commitGroupLabelEdit = () => {
    onGroupLabelChange(node.id, groupLabelDraft.trim());
    setIsEditingGroupLabel(false);
  };

  const onGroupLabelBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!isEditingGroupLabel) return;
    const next = e.relatedTarget;
    if (next instanceof Node && groupLabelEditRef.current?.contains(next)) return;
    endGroupLabelEdit();
  };

  return (
    <AccordionItem
      value={node.id}
      data-policy-tree-node="group"
      className={cn(
        "border-0 bg-transparent shadow-none",
        draggedRuleId ? "rounded-lg ring-1 ring-blue-300" : undefined
      )}
      onDragEnter={readOnly ? undefined : (e: React.DragEvent<HTMLDivElement>) => onGroupDragOver(node.id, e)}
      onDragOver={readOnly ? undefined : (e: React.DragEvent<HTMLDivElement>) => onGroupDragOver(node.id, e)}
      onDrop={readOnly ? undefined : (e: React.DragEvent<HTMLDivElement>) => onGroupDropRule(node.id, e)}
    >
      <AccordionTrigger className={POLICY_ACCORDION_TRIGGER_CLASS}>
        <div
          data-policy-tree-trigger-main=""
          className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2"
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
            {isEditingGroupLabel && !readOnly ? (
              <div
                ref={groupLabelEditRef}
                className="flex shrink-0 flex-nowrap items-end gap-2"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <Input
                  value={groupLabelDraft}
                  onChange={(e) => setGroupLabelDraft(e.target.value)}
                  onBlur={onGroupLabelBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitGroupLabelEdit();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      endGroupLabelEdit();
                    }
                  }}
                  autoFocus
                  placeholder="Group name"
                  className={cn(POLICY_TREE_INPUT_CLASS, "w-[220px] max-w-[220px] shrink-0")}
                />
                <SimulatorInlineEditActions onSave={commitGroupLabelEdit} onCancel={endGroupLabelEdit} />
              </div>
            ) : (
              <div className="flex items-center gap-1">
                {canJumpToSourcePick && !readOnly ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className={cn(POLICY_TREE_GROUP_TITLE_CLASS, "h-auto p-0")}
                    onClick={(e) => {
                      e.stopPropagation();
                      onGoToSourcePick(node.sourcePickId!);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    {`Group | ${groupTitle}`}
                  </Button>
                ) : (
                  <span className={POLICY_TREE_GROUP_TITLE_CLASS}>{`Group | ${groupTitle}`}</span>
                )}
                {!readOnly ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn("size-7 shrink-0", POLICY_TREE_SUBTLE_TEXT_CLASS)}
                    aria-label="Edit group title"
                    onClick={(e) => {
                      e.stopPropagation();
                      setGroupLabelDraft(node.label ?? "");
                      setIsEditingGroupLabel(true);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <EditIcon size={16} aria-hidden />
                  </Button>
                ) : null}
              </div>
            )}
            {!hideActionForNode ? (
              readOnly ? (
                <PolicyActionField
                  actionLabel={
                    formatPolicyActionDisplayLabel(node.action ?? defaultGroupAction) ?? "Keep Only"
                  }
                />
              ) : (
                <div
                  data-policy-group-action=""
                  data-policy-group-action-value={node.action ?? defaultGroupAction}
                  className="w-fit min-w-0"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <TradeFilterSelect
                    layout="fit"
                    fitToLongestOption
                    value={node.action ?? defaultGroupAction}
                    onValueChange={(value) => onGroupActionChange(node.id, value as TradePolicyAction)}
                    options={GROUP_POLICY_ACTION_OPTIONS}
                    disabled={readOnly}
                  />
                </div>
              )
            ) : null}
            <div
              className="min-w-[10rem]"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <TradeFilterSelectFullWidth
                value={node.op}
                onValueChange={(value) => onGroupOpChange(node.id, value as TradePolicyGroupNode["op"])}
                options={[
                  { value: "and", label: "All of (AND)" },
                  { value: "or", label: "Any of (OR)" },
                ]}
                disabled={readOnly}
              />
            </div>
          </div>
          {!readOnly ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              style={{ color: TRADE_LOG_LOSS_RED }}
              aria-label="Remove group"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(node.id);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={!canRemoveGroup}
            >
              <TrashIcon size={18} aria-hidden />
            </Button>
          ) : null}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pt-0 pb-0 text-foreground">
        <PolicyTreeNodesAccordion
          {...props}
          nodes={node.children}
          canRemoveGroup
          nested
        />
        {!readOnly && !isPolicyCombineShellGroup(node) ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <SimulatorPolicyAddButton kind="rule" onClick={() => onAddRule(node.id)} />
          </div>
        ) : null}
      </AccordionContent>
    </AccordionItem>
  );
}

export function PolicyTreeNodesAccordion(
  props: PolicyTreeEditorProps & { nested?: boolean }
): React.ReactElement | null {
  const { nodes, collapsedNodeIds, setCollapsedNodeIds, nested } = props;
  if (nodes.length === 0) return null;

  const openIds = nodes.filter((n) => !collapsedNodeIds.has(n.id)).map((n) => n.id);

  return (
    <Accordion
      type="multiple"
      value={openIds}
      onValueChange={(next) => syncSiblingAccordionOpen(nodes, next, setCollapsedNodeIds)}
      className={cn(POLICY_NESTED_ACCORDION_CLASS, !nested && "mb-0")}
      data-policy-tree-accordion=""
      data-policy-tree-accordion-root={nested ? undefined : ""}
    >
      {nodes.map((node) =>
        node.kind === "rule" ? (
          <PolicyRuleAccordionItem key={node.id} node={node} props={props} />
        ) : (
          <PolicyGroupAccordionItem key={node.id} node={node} props={props} />
        )
      )}
    </Accordion>
  );
}
